import React from 'react';
import { Table, Button, Spinner } from 'react-bootstrap';
import { SurveysAPI } from '../../API';
import { inFieldFilter, todayInIST, InFieldFilter } from '../../Utils/istDateFilter';

type Props = {
    surveyId: string;
    // Parent survey's projectname (e.g. "DXD_TESTING_5555"). Used to strip the
    // `${parentName}_${shortName}_` prefix from each row's `name` so the Market
    // column shows just the PM-typed suffix (e.g. "ENGLISH") instead of the
    // full stored value. Optional — when absent, the suffix-derivation safely
    // returns '' and no differentiator is rendered.
    parentName?: string;
    // Called with the From / To dates and Status each time the grid is
    // (re)loaded, so the page's partner xlsx export applies the same filter
    // and lists exactly the grid's partner rows (DXD-7359).
    onFilterApplied?: (filter: InFieldFilter) => void;
};

// Mirrors the suffix derivation in PartnersTab/dashboardpartners/ChildSurveyTab.
// Returns the PM-typed suffix or '' when the stored value is the bare base
// prefix, missing, or in an unknown shape — callers gate the dot separator on
// the empty-string result.
function extractDisplaySuffix(fullName: string | null | undefined, parentName: string, shortName: string): string {
    if (!fullName) return '';
    const trimmed = String(fullName).trim();
    if (!trimmed) return '';
    const base = `${parentName}_${shortName}`;
    const fullPrefix = `${base}_`;
    if (trimmed === base) return '';
    if (trimmed.startsWith(fullPrefix)) return trimmed.substring(fullPrefix.length);
    const orphanBase = `_${shortName}`;
    const orphanPrefix = `_${shortName}_`;
    if (trimmed === orphanBase) return '';
    if (trimmed.startsWith(orphanPrefix)) return trimmed.substring(orphanPrefix.length);
    
    // Fallback: If parent name changed, the string might be `OldParent_IN_Suffix`.
    const fallbackPrefix = `_${shortName}_`;
    const lastIndex = trimmed.lastIndexOf(fallbackPrefix);
    if (lastIndex !== -1) {
        return trimmed.substring(lastIndex + fallbackPrefix.length);
    }
    if (trimmed.endsWith(`_${shortName}`)) return '';
    
    return '';
}

type Row = {
    rowKey: string;
    surveyId: string;
    name: string;
    // Free-form PM-typed label (own DB column). Carries the actual suffix
    // (e.g. "DXD_TESTING_5555_IN_ENGLISH") that the Market cell strips down
    // to "ENGLISH". `name` is the auto-generated baseline (e.g. just
    // "DXD_TESTING_5555_IN") so suffix extraction MUST prefer this field.
    // Nullable — legacy rows fall back to `name`, which the helper safely
    // resolves to empty suffix.
    childProjectName?: string | null;
    shortName: string;
    country: string;
    status: string;
    completes: number;
    terminates: number;
    overQuota: number;
    qualityTerminated: number;
    preScreenTerminated: number;
    verisoulBlocked?: number;
    verisoul_terminate_count?: number;
    verisoul?: boolean;
    dropouts: number;
    totalHits: number;
};

type State = {
    fromDate: string;   // 'YYYY-MM-DD' or '' — an India calendar day, sent as-is
    toDate: string;     // 'YYYY-MM-DD' or '' — inclusive
    selectedStatus: string;  // 'All' | 'Test' | 'Open' | 'Hold' | 'Wait' | 'Close'
    stats: Row[];
    loading: boolean;
    error: string | null;
};

// Mirror the survey-form Survey Status radio set. Matches surveys.surveyType
// values stamped on assignedSurveys.surveyType at insertion time.
export const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'All',   label: 'All' },
    { value: 'Test',  label: 'Test' },
    { value: 'Open',  label: 'Live' },
    { value: 'Hold',  label: 'On Hold' },
    { value: 'Wait',  label: 'Ids Awaited' },
    { value: 'Close', label: 'End' },
];

export class InFieldStats extends React.Component<Props, State> {
    _mounted = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            fromDate: '',
            toDate: '',
            selectedStatus: 'All',
            stats: [],
            loading: false,
            error: null,
        };
    }

    componentDidMount() {
        this._mounted = true;
        this.fetchStats();
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    fetchStats = () => {
        if (!this.props.surveyId) return;
        this.setState({ loading: true, error: null });
        // Plain dates, no Date/ISO conversion: the backend cuts days at India
        // midnight, so the browser timezone cannot move the window (DXD-7359).
        const filter = inFieldFilter(this.state.fromDate, this.state.toDate, this.state.selectedStatus);
        if (this.props.onFilterApplied) this.props.onFilterApplied(filter);
        SurveysAPI.getInFieldStats(this.props.surveyId, filter.fromDate, filter.toDate, this.state.selectedStatus)
            .then((data: any) => {
                if (!this._mounted) return;
                this.setState({ stats: data?.stats || [], loading: false });
            })
            .catch((err: any) => {
                if (!this._mounted) return;
                this.setState({ error: err?.message || 'Failed to load in-field stats', loading: false });
            });
    };

    render() {
        const { stats, fromDate, toDate, selectedStatus, loading, error } = this.state;
        const statusLabel = (val: string): string => {
            const opt = STATUS_OPTIONS.find(o => o.value === val);
            return opt ? opt.label : (val || '-');
        };

        const totals = stats.reduce(
            (acc, r) => {
                const rowVerisoul = r.verisoulBlocked ?? r.verisoul_terminate_count ?? 0;
                return {
                    completes: acc.completes + (r.completes || 0),
                    terminates: acc.terminates + (r.terminates || 0),
                    overQuota: acc.overQuota + (r.overQuota || 0),
                    qualityTerminated: acc.qualityTerminated + (r.qualityTerminated || 0),
                    preScreenTerminated: acc.preScreenTerminated + (r.preScreenTerminated || 0),
                    verisoulBlocked: acc.verisoulBlocked + rowVerisoul,
                    dropouts: acc.dropouts + (r.dropouts || 0),
                    totalHits: acc.totalHits + (r.totalHits || 0),
                };
            },
            { completes: 0, terminates: 0, overQuota: 0, qualityTerminated: 0, preScreenTerminated: 0, verisoulBlocked: 0, dropouts: 0, totalHits: 0 },
        );

        return (
            <div className="jumbotron bg-white p-3 border shadow-sm mt-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">In-Field Stats <small className="text-muted">(live fieldwork by market)</small></h6>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>

                <div className="row align-items-end mb-3">
                    <div className="col-md-3">
                        <label className="mb-1" htmlFor="infield-from">From <small className="text-muted">(IST)</small></label>
                        <input
                            id="infield-from"
                            type="date"
                            className="form-control form-control-sm"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={(e) => this.setState({ fromDate: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="mb-1" htmlFor="infield-to">To <small className="text-muted">(IST, inclusive)</small></label>
                        <input
                            id="infield-to"
                            type="date"
                            className="form-control form-control-sm"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={(e) => this.setState({ toDate: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="mb-1" htmlFor="infield-status">Status</label>
                        <select
                            id="infield-status"
                            className="form-control form-control-sm"
                            value={selectedStatus}
                            onChange={(e) => this.setState({ selectedStatus: e.target.value })}
                        >
                            {STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <Button size="sm" variant="primary" onClick={this.fetchStats} disabled={loading}>
                            Filter
                        </Button>{' '}
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                                const today = todayInIST();
                                this.setState(
                                    { fromDate: today, toDate: today, selectedStatus: 'All' },
                                    this.fetchStats,
                                );
                            }}
                        >
                            Today
                        </Button>{' '}
                        {(fromDate || toDate) && (
                            <Button
                                size="sm"
                                variant="outline-dark"
                                onClick={() => {
                                    this.setState(
                                        { fromDate: '', toDate: '', selectedStatus: 'All' },
                                        this.fetchStats,
                                    );
                                }}
                            >
                                All Time
                            </Button>
                        )}
                    </div>
                </div>

                {error && <div className="alert alert-danger py-2 mb-2">{error}</div>}

                <Table responsive size="sm" bordered>
                    <thead className="thead-light">
                        <tr>
                            <th>Market</th>
                            <th>Status</th>
                            <th>Completes</th>
                            <th>Terminates</th>
                            <th>Over Quota</th>
                            <th>Quality Term.</th>
                            <th>PQ Terminated</th>
                            <th>Verisoul Blocked</th>
                            <th>Dropouts</th>
                            <th>Total Hits</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.length === 0 && !loading ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted">No activity in selected window</td>
                            </tr>
                        ) : (
                            stats.map((r) => (
                                // rowKey is composite (surveyId × status) — surveyId
                                // alone collides when a market has multiple status rows.
                                <tr key={r.rowKey || `${r.surveyId}::${r.status}`}>
                                    <td>
                                        <strong>{r.shortName}</strong>
                                        {(() => {
                                            // Show only the PM-typed suffix (e.g. "ENGLISH")
                                            // next to the shortName, never the full stored name.
                                            // No suffix => no differentiator at all. The legacy
                                            // dupCountries gate is intentionally dropped: rows
                                            // with a suffix always show it, rows without never
                                            // do, no special-casing for duplicates.
                                            const suffix = extractDisplaySuffix(r.childProjectName || r.name, this.props.parentName || '', r.shortName);
                                            return suffix ? (
                                                <span className="text-muted ml-2" style={{ fontSize: '0.85em' }}>
                                                    - {suffix}
                                                </span>
                                            ) : null;
                                        })()}
                                    </td>
                                    <td>{statusLabel(r.status)}</td>
                                    <td>{r.completes}</td>
                                    <td>{r.terminates}</td>
                                    <td>{r.overQuota}</td>
                                    <td>{r.qualityTerminated}</td>
                                    <td>{r.preScreenTerminated}</td>
                                    <td>{r.verisoulBlocked ?? r.verisoul_terminate_count ?? 0}</td>
                                    <td>{r.dropouts}</td>
                                    <td>{r.totalHits}</td>
                                </tr>
                            ))
                        )}
                        {stats.length > 0 && (
                            <tr className="font-weight-bold bg-light">
                                <td colSpan={2}>Total</td>
                                <td>{totals.completes}</td>
                                <td>{totals.terminates}</td>
                                <td>{totals.overQuota}</td>
                                <td>{totals.qualityTerminated}</td>
                                <td>{totals.preScreenTerminated}</td>
                                <td>{totals.verisoulBlocked}</td>
                                <td>{totals.dropouts}</td>
                                <td>{totals.totalHits}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    }
}

export default InFieldStats;
